/** Sends V1-compatible Trade-In analytics events and the Create2Stay front-door signal. */
import ReactGA from 'react-ga'
import ReactGA4 from 'react-ga4'
import { useAuthStore } from '../store/auth'

export type AnalyticsData = Record<string, string | undefined>

export enum AnalyticsEvent {
  trackAppStart = 'trackAppStart',
  trackStepChange = 'trackStepChange',
  trackCompletion = 'trackCompletion',
}

interface AnalyticsStartOptions {
  shopId: string
  uaTrackingId?: string | null
  gaTrackingId?: string | null
}

const EVENT_CATEGORIES: Record<AnalyticsEvent, string> = {
  [AnalyticsEvent.trackAppStart]: 'Initialization',
  [AnalyticsEvent.trackStepChange]: 'Navigation',
  [AnalyticsEvent.trackCompletion]: 'TradeIn',
}

let bootstrapShopId: string | null = null
let ga4Enabled = false
let uaEnabled = false

/** Emits one event to whichever per-shop Google Analytics trackers were configured. */
const event = (analyticsEvent: AnalyticsEvent, data: AnalyticsData = {}): void => {
  const state = useAuthStore.getState()
  const category = EVENT_CATEGORIES[analyticsEvent]

  if (ga4Enabled) {
    const payload = {
      ...data,
      event: analyticsEvent,
      area: category,
      shopId: state.shopId ?? bootstrapShopId ?? undefined,
      locale: state.config.locale,
      currency: state.config.currency,
      consumerId: state.consumerId ?? undefined,
      wholeSaleActive: state.config.wholeSaleActive,
      allowedCountry: state.config.allowedCountry,
    }
    try {
      ReactGA4.event({ action: analyticsEvent, category }, payload)
    } catch (error) {
      console.error('GA4 Event failed', payload, error)
    }
  }

  if (uaEnabled) {
    const payload = { category, action: analyticsEvent, nonInteraction: true }
    try {
      ReactGA.event(payload, ['create2STAY'])
    } catch (error) {
      console.error('UA Event failed', payload, error)
    }
  }
}

/** Initializes per-shop trackers, notifies Create2Stay, and records the app opening. */
const start = ({ shopId, uaTrackingId, gaTrackingId }: AnalyticsStartOptions): void => {
  bootstrapShopId = shopId
  ga4Enabled = Boolean(gaTrackingId)
  uaEnabled = Boolean(uaTrackingId)

  if (uaTrackingId) ReactGA.initialize(uaTrackingId)
  if (gaTrackingId) ReactGA4.initialize(gaTrackingId)

  try {
    void fetch(`https://micros.create2stay.com/api/frontdoor/track?tf=${shopId}`, {
      method: 'GET',
    }).catch(() => undefined)
  } catch {
    // Analytics is best-effort and must never interrupt the trade-in flow.
  }

  event(AnalyticsEvent.trackAppStart)
}

export const Analytics = { event, start }
